/**
 * Cart Import/Export for B2B customers.
 * Supports CSV and XLSX formats.
 * Uses SheetJS (xlsx) library for file parsing and generation.
 */

let xlsxPromise = null;

function loadXLSX() {
  if (window.XLSX) return Promise.resolve(window.XLSX);
  if (xlsxPromise) return xlsxPromise;

  xlsxPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => resolve(window.XLSX);
    script.onerror = () => {
      xlsxPromise = null;
      reject(new Error('Failed to load XLSX library'));
    };
    document.head.appendChild(script);
  });

  return xlsxPromise;
}

const _delay = (ms) => new Promise((r) => setTimeout(r, ms));

async function _cartVariantQty(variantId) {
  const resp = await fetch(`${Shopify.routes.root}cart.js`);
  if (!resp.ok) return 0;
  const cart = await resp.json();
  return cart.items.filter((i) => i.variant_id === variantId).reduce((sum, i) => sum + i.quantity, 0);
}

async function _fetchRetry(url, opts = {}, retries = 5) {
  for (let attempt = 0; attempt < retries; attempt++) {
    const resp = await fetch(url, opts);
    if (resp.status === 429) {
      const wait = Math.min(2000 * Math.pow(2, attempt), 16000);
      await _delay(wait);
      continue;
    }
    return resp;
  }
  return fetch(url, opts);
}

document.addEventListener('alpine:init', () => {
  Alpine.data('xCartImportExport', () => ({
    showExportDropdown: false,
    showImportModal: false,
    exporting: false,

    // 'file' = import from CSV/XLSX (sets quantities), 'quick' = B2B quick order
    // typed into a SKU/quantity table (adds on top of what's already in the cart).
    // Both share the same lookup → review → add pipeline and modal.
    importMode: 'file',
    quickRows: [],

    // Import state: upload | processing | review | importing | done
    importStep: 'upload',
    importProgress: '',
    importErrors: [],
    importWarnings: [],
    itemsToImport: [],
    importedCount: 0,
    dragOver: false,

    // Translations loaded from DOM
    i18n: {},

    // Per-item formatted prices (price / VOC / campaign) loaded from DOM, keyed by variant_id
    itemPrices: {},

    init() {
      const el = document.getElementById('cart-ie-i18n');
      if (el) {
        try {
          this.i18n = JSON.parse(el.textContent);
        } catch (e) {
          /* noop */
        }
      }

      const pricesEl = document.getElementById('cart-ie-prices');
      if (pricesEl) {
        try {
          this.itemPrices = JSON.parse(pricesEl.textContent);
        } catch (e) {
          /* noop */
        }
      }
    },

    t(key) {
      return this.i18n[key] || key;
    },

    // ─── EXPORT ──────────────────────────────────────────────

    async exportCart(format) {
      this.exporting = true;
      this.showExportDropdown = false;

      try {
        const XLSX = await loadXLSX();

        const cartResp = await fetch(`${Shopify.routes.root}cart.js`);
        const cart = await cartResp.json();

        const isB2b = window.Eurus?.customerB2b === true;

        const headers = [
          this.t('col_sku'),
          this.t('col_gtin'),
          this.t('col_quantity'),
          this.t('col_title'),
          this.t('col_price'),
        ];
        if (isB2b) {
          headers.push(this.t('col_voc'), this.t('col_campaign_price'));
        }

        const rows = [headers];

        if (cart.items.length > 0) {
          // Fetch products for barcode data
          const productCache = {};
          const handles = [...new Set(cart.items.map((i) => i.handle))];

          await Promise.all(
            handles.map(async (handle) => {
              try {
                const r = await fetch(`${Shopify.routes.root}products/${encodeURIComponent(handle)}.js`);
                if (r.ok) productCache[handle] = await r.json();
              } catch {
                /* skip */
              }
            })
          );

          const isCsv = format === 'csv';
          const priceValue = (raw, fmt) => {
            if (raw == null) return '';
            return isCsv ? raw / 100 : fmt;
          };

          for (const item of cart.items) {
            const product = productCache[item.handle];
            const variant = product?.variants?.find((v) => v.id === item.variant_id);

            const prices = this.itemPrices[item.variant_id];

            const row = [
              item.sku || '',
              variant?.barcode || '',
              item.quantity,
              item.product_title +
                (item.variant_title && item.variant_title !== 'Default Title' ? ` - ${item.variant_title}` : ''),
              priceValue(prices?.price_raw, prices?.price_fmt),
            ];
            if (isB2b) {
              row.push(
                priceValue(prices?.voc_raw, prices?.voc_fmt),
                priceValue(prices?.campaign_raw, prices?.campaign_fmt)
              );
            }
            rows.push(row);
          }
        }

        const ws = XLSX.utils.aoa_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Cart');

        const filename = `kosik-export.${format}`;
        XLSX.writeFile(wb, filename, { bookType: format === 'csv' ? 'csv' : 'xlsx' });
      } catch (err) {
        console.error('Cart export failed:', err);
        this.importErrors = [this.t('export_failed')];
      } finally {
        this.exporting = false;
      }
    },

    // ─── IMPORT ──────────────────────────────────────────────

    openFileImport() {
      this.importMode = 'file';
      this.resetImport();
      this.showImportModal = true;
    },

    // ─── QUICK ORDER (B2B) ───────────────────────────────────

    openQuickOrder() {
      this.importMode = 'quick';
      this.resetImport();
      this.quickRows = Array.from({ length: 5 }, () => ({ sku: '', qty: '' }));
      this.showImportModal = true;
    },

    addQuickRow() {
      this.quickRows.push({ sku: '', qty: '' });
    },

    // Enter behaves like Tab in the quick order table (it never submits the form).
    focusNextQuickInput(event) {
      const inputs = [...event.target.form.querySelectorAll('input')];
      const next = inputs[inputs.indexOf(event.target) + 1];
      if (next) next.focus();
    },

    // Reaching the second-to-last row reveals another empty one, so there's always a spare row.
    ensureSpareRow(index) {
      if (index >= this.quickRows.length - 2) this.addQuickRow();
    },

    removeQuickRow(index) {
      this.quickRows.splice(index, 1);
      if (this.quickRows.length === 0) this.addQuickRow();
    },

    // Back from review to the first step; in quick mode the typed rows are kept.
    backToUpload() {
      this.importStep = 'upload';
      this.importErrors = [];
      this.importWarnings = [];
      this.itemsToImport = [];
    },

    async submitQuickOrder() {
      const rows = this.quickRows
        .map((row, i) => ({ identifier: String(row.sku).trim(), rawQty: row.qty, srcRow: i + 1 }))
        .filter((row) => row.identifier !== '');

      this.importStep = 'processing';
      this.importErrors = [];
      this.importWarnings = [];
      this.itemsToImport = [];

      if (rows.length === 0) {
        this.importErrors.push(this.t('err_no_valid_rows'));
        this.importStep = 'review';
        return;
      }

      try {
        await this._validateAndLookup(rows);
      } catch (err) {
        console.error('Quick order processing failed:', err);
        this.importErrors.push(this.t('err_import_failed'));
        this.importStep = 'review';
      }
    },

    closeImportModal() {
      this.showImportModal = false;
      this.resetImport();
    },

    resetImport() {
      this.importStep = 'upload';
      this.importProgress = '';
      this.importErrors = [];
      this.importWarnings = [];
      this.itemsToImport = [];
      this.importedCount = 0;
      this.dragOver = false;
    },

    handleFileDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer?.files?.[0];
      if (file) this.processFile(file);
    },

    handleFileSelect(e) {
      const file = e.target?.files?.[0];
      if (file) this.processFile(file);
    },

    async processFile(file) {
      this.importStep = 'processing';
      this.importErrors = [];
      this.importWarnings = [];
      this.itemsToImport = [];

      try {
        const XLSX = await loadXLSX();

        // 1. Validate file extension
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(ext)) {
          this.importErrors.push(this.t('err_unsupported_format'));
          this.importStep = 'review';
          return;
        }

        this.importProgress = this.t('progress_reading');

        // 2. Parse file
        const data = await file.arrayBuffer();
        let workbook;
        try {
          workbook = XLSX.read(data);
        } catch {
          this.importErrors.push(this.t('err_parse_failed'));
          this.importStep = 'review';
          return;
        }

        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!sheet) {
          this.importErrors.push(this.t('err_empty_file'));
          this.importStep = 'review';
          return;
        }

        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // 3. Validate structure: need header + at least 1 data row
        if (rows.length < 2) {
          this.importErrors.push(this.t('err_no_data'));
          this.importStep = 'review';
          return;
        }

        // Data rows: skip header, filter empty rows
        const dataRows = rows.slice(1).filter((row) => row[0] != null && String(row[0]).trim() !== '');

        if (dataRows.length === 0) {
          this.importErrors.push(this.t('err_no_valid_rows'));
          this.importStep = 'review';
          return;
        }

        // 4. Columns: SKU, GTIN, Quantity, ... (srcRow: 1-indexed, +1 for header)
        await this._validateAndLookup(
          dataRows.map((row, i) => ({ identifier: String(row[0]).trim(), rawQty: row[2], srcRow: i + 2 }))
        );
      } catch (err) {
        console.error('Cart import processing failed:', err);
        this.importErrors.push(this.t('err_import_failed'));
        this.importStep = 'review';
      }
    },

    // Shared by file import and quick order: validates quantities, merges duplicate
    // identifiers, looks each one up by SKU/barcode and leaves the result in
    // itemsToImport / importErrors / importWarnings with importStep = 'review'.
    async _validateAndLookup(rows) {
      const parsedRows = [];

      for (const row of rows) {
        const { identifier, rawQty, srcRow } = row;
        const quantity = parseInt(rawQty, 10);

        if (!identifier) {
          this.importWarnings.push(`${this.t('row')} ${srcRow}: ${this.t('err_missing_id')}`);
          continue;
        }

        if (isNaN(quantity) || quantity <= 0) {
          this.importWarnings.push(`${this.t('row')} ${srcRow}: ${this.t('err_invalid_qty')} "${identifier}"`);
          continue;
        }

        parsedRows.push({ identifier, quantity, srcRow });
      }

      if (parsedRows.length === 0) {
        this.importErrors.push(this.t('err_no_valid_rows'));
        this.importStep = 'review';
        return;
      }

      // 5. Aggregate duplicates (sum quantities for same identifier)
      const aggregated = new Map();
      for (const row of parsedRows) {
        const key = row.identifier;
        if (aggregated.has(key)) {
          const existing = aggregated.get(key);
          existing.quantity += row.quantity;
        } else {
          aggregated.set(key, { ...row });
        }
      }
      const uniqueRows = [...aggregated.values()];

      // 6. Look up products
      const productCache = {};
      const notFound = [];
      const unavailable = [];
      const toAdd = [];
      const total = uniqueRows.length;

      // Process lookups sequentially to avoid 429 rate limits
      for (let i = 0; i < total; i++) {
        this.importProgress = `${this.t('progress_searching')} (${i + 1}/${total})...`;
        await this._lookupVariant(uniqueRows[i], toAdd, notFound, unavailable, productCache);
        if (i < total - 1) await _delay(300);
      }

      // 7. Report results
      if (notFound.length > 0) {
        const list = notFound.map((nf) => `"${nf.identifier}" (${this.t('row')} ${nf.srcRow})`).join(', ');
        this.importErrors.push(`${this.t('err_products_not_found')}: ${list}`);
      }

      if (unavailable.length > 0) {
        const list = unavailable.map((ua) => `"${ua.identifier}" (${this.t('row')} ${ua.srcRow})`).join(', ');
        this.importWarnings.push(`${this.t('warn_products_unavailable')}: ${list}`);
      }

      this.itemsToImport = toAdd;
      this.importProgress = '';
      this.importStep = 'review';
    },

    async _lookupVariant(row, toAdd, notFound, unavailable, productCache) {
      const { identifier, quantity, srcRow } = row;

      try {
        const params = new URLSearchParams({
          q: identifier,
          'resources[type]': 'product',
          'resources[options][fields]': 'variants.sku,variants.barcode',
          'resources[limit]': '10',
        });

        const resp = await _fetchRetry(`${Shopify.routes.root}search/suggest.json?${params}`);

        if (!resp.ok) {
          notFound.push({ identifier, srcRow });
          return;
        }

        const json = await resp.json();

        const products = json?.resources?.results?.products || [];

        if (products.length === 0) {
          notFound.push({ identifier, srcRow });
          return;
        }

        // Fetch full product data and match variant by SKU or barcode
        for (const product of products) {
          const handle = product.handle;
          if (!handle) continue;

          let fullProduct = productCache[handle];
          if (!fullProduct) {
            try {
              const prodResp = await _fetchRetry(`${Shopify.routes.root}products/${encodeURIComponent(handle)}.js`);
              if (!prodResp.ok) continue;
              fullProduct = await prodResp.json();
              productCache[handle] = fullProduct;
            } catch {
              continue;
            }
          }

          const variant = fullProduct.variants.find(
            (v) => String(v.sku) === identifier || String(v.barcode) === identifier
          );

          if (variant) {
            if (!variant.available) {
              unavailable.push({ identifier, srcRow, variantId: variant.id, title: fullProduct.title });
            } else {
              toAdd.push({ identifier, quantity, srcRow, variantId: variant.id, title: fullProduct.title });
            }
            return;
          }
        }

        notFound.push({ identifier, srcRow });
      } catch (err) {
        console.error(`Lookup failed for "${identifier}":`, err);
        notFound.push({ identifier, srcRow });
      }
    },

    async confirmImport() {
      if (this.itemsToImport.length === 0) return;

      this.importStep = 'importing';
      this.importProgress = `${this.t('progress_adding')} (0/${this.itemsToImport.length})...`;

      try {
        // Fetch current cart to know existing quantities
        const cartResp = await _fetchRetry(`${Shopify.routes.root}cart.js`);
        const currentCart = cartResp.ok ? await cartResp.json() : { items: [] };

        // Build a map of existing variant data (key + quantity)
        const existingItems = new Map();
        const existingQty = new Map();
        for (const item of currentCart.items) {
          existingItems.set(item.variant_id, {
            key: item.key,
            quantity: item.quantity,
          });
          existingQty.set(item.variant_id, (existingQty.get(item.variant_id) || 0) + item.quantity);
        }

        // For items already in cart, use cart/update.js to set new quantities (bulk)
        // For new items, use cart/add.js (bulk)
        const toUpdate = [];
        const toAdd = [];

        for (const item of this.itemsToImport) {
          const existing = existingItems.get(item.variantId);
          // Quick order adds on top of the cart (cart/add.js increments existing lines
          // and still enforces stock); only a file import replaces quantities.
          if (existing && this.importMode !== 'quick') {
            toUpdate.push({
              ...item,
            });
          } else {
            toAdd.push(item);
          }
        }

        let addedCount = 0;
        const failedItems = [];

        async function _extractReason(resp) {
          try {
            const data = await resp.json();
            return data.description || data.message || `HTTP ${resp.status}`;
          } catch {
            return `HTTP ${resp.status}`;
          }
        }

        // Try to add an item; if 422 due to stock limit, retry with max available qty
        const _addWithStockCheck = async (item, self) => {
          // File import zeroes existing lines first, so it starts from 0; quick order adds on top.
          const beforeQty = self.importMode === 'quick' ? existingQty.get(item.variantId) || 0 : 0;
          const resp = await _fetchRetry(`${Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: item.variantId, quantity: item.quantity }] }),
          });
          if (resp.ok) return { ok: true, adjusted: false };

          if (resp.status === 422) {
            // Parse max available qty from Shopify error (e.g. "You can only add 1 of...")
            try {
              const errData = await resp.json();
              const desc = errData.description || '';

              // Shopify now adds as many as stock allows and still answers 422, so check
              // the cart before calling the line failed (the retry below would only 422 again).
              const added = (await _cartVariantQty(item.variantId)) - beforeQty;
              if (added > 0) {
                self.importWarnings.push(`${self.t('warn_qty_adjusted')}: "${item.identifier}" ${item.quantity} → ${added}`);
                return { ok: true, adjusted: true };
              }

              const match = desc.match(/(\d+)/);
              if (match) {
                const maxQty = parseInt(match[1], 10);
                if (maxQty > 0 && maxQty < item.quantity) {
                  await _delay(500);
                  const retryResp = await _fetchRetry(`${Shopify.routes.root}cart/add.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: [{ id: item.variantId, quantity: maxQty }] }),
                  });
                  if (retryResp.ok) {
                    self.importWarnings.push(
                      `${self.t('warn_qty_adjusted')}: "${item.identifier}" ${item.quantity} → ${maxQty}`
                    );
                    return { ok: true, adjusted: true };
                  }
                }
              }
              return { ok: false, reason: desc || `HTTP 422` };
            } catch {
              return { ok: false, reason: 'HTTP 422' };
            }
          }

          const reason = await _extractReason(resp);
          return { ok: false, reason };
        };

        // Remove existing items from cart first (set qty to 0), then re-add via cart/add.js
        // which enforces stock limits (cart/update.js does NOT enforce stock limits)
        if (toUpdate.length > 0) {
          this.importProgress = `${this.t('progress_adding')} (${toUpdate.length}/${this.itemsToImport.length})...`;
          const zeroes = {};
          for (const item of toUpdate) {
            zeroes[item.variantId] = 0;
          }
          try {
            const zeroResp = await _fetchRetry(`${Shopify.routes.root}cart/update.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: zeroes }),
            });
            if (zeroResp.ok) {
              // Items removed — move them to toAdd so cart/add.js handles stock enforcement
              toAdd.push(...toUpdate);
              toUpdate.length = 0;
            } else {
              // Bulk zero failed — fall back to individual cart/change.js calls to remove
              for (const item of toUpdate) {
                const existing = existingItems.get(item.variantId);
                if (!existing) continue;
                try {
                  await _delay(1000);
                  const singleResp = await _fetchRetry(`${Shopify.routes.root}cart/change.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: existing.key, quantity: 0 }),
                  });
                  if (singleResp.ok) {
                    toAdd.push(item);
                  } else {
                    const reason = await _extractReason(singleResp);
                    failedItems.push({ identifier: item.identifier, reason });
                  }
                } catch (e) {
                  failedItems.push({ identifier: item.identifier, reason: e.message || 'Network error' });
                }
              }
              toUpdate.length = 0;
            }
          } catch (e) {
            for (const item of toUpdate) {
              failedItems.push({ identifier: item.identifier, reason: e.message || 'Network error' });
            }
          }
        }

        // Add items one by one via _addWithStockCheck so each gets proper 422 stock handling
        if (toAdd.length > 0) {
          for (let i = 0; i < toAdd.length; i++) {
            const item = toAdd[i];
            this.importProgress = `${this.t('progress_adding')} (${addedCount + failedItems.length + 1}/${this.itemsToImport.length})...`;
            try {
              if (i > 0) await _delay(500);
              const result = await _addWithStockCheck(item, this);
              if (result.ok) {
                addedCount++;
              } else {
                failedItems.push({ identifier: item.identifier, reason: result.reason });
              }
            } catch (e) {
              failedItems.push({ identifier: item.identifier, reason: e.message || 'Network error' });
            }
          }
        }

        if (failedItems.length > 0) {
          for (const f of failedItems) {
            this.importWarnings.push(`${this.t('warn_add_failed')}: "${f.identifier}" — ${f.reason}`);
          }
        }

        this.importedCount = addedCount;
        this.importStep = 'done';
        this.importProgress = '';
      } catch (err) {
        console.error('Cart import failed:', err);
        this.importErrors.push(this.t('err_import_failed'));
        this.importStep = 'review';
      }
    },
  }));
});
